import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Sidebar } from './Sidebar';
import { MobileNavBar } from './MobileNavBar';
import { NotificationsDropdown } from './header/NotificationsDropdown';
import { TeamChatButton } from '@/modules/team-chat/components/TeamChatButton';
import { DisconnectedChannelWidget } from './DisconnectedChannelWidget';
import { RouteAccessGuard } from './RouteAccessGuard';

/** Título da m-appbar mobile por prefixo de rota (mockup: "CRM", "Atendimento", …). */
const ROUTE_TITLES: Array<[string, string]> = [
  ['/dashboard/management/sales', 'Minhas Vendas'],
  ['/crm/ranking', 'Ranking'],
  ['/crm', 'CRM'],
  ['/chat/management', 'Atendimentos'],
  ['/chat', 'Atendimento'],
  ['/calendar', 'Agenda'],
  ['/helpdesk/tickets', 'Tickets'], // específica antes de /helpdesk (t1="Tickets", t2="Help Desk · {pipeline}")
  ['/helpdesk', 'Help Desk'],
  ['/automation', 'Automação'],
  ['/prospecting', 'Prospecção'],
  ['/reports', 'Relatórios'],
  ['/dashboard', 'Dashboards'],
  ['/databases', 'Bases de dados'],
  ['/websites', 'Websites'],
  ['/email', 'E-mail'],
  ['/settings', 'Configurações'],
  ['/billing', 'Faturamento'],
];

function getRouteTitle(pathname: string, appName?: string): string {
  const hit = ROUTE_TITLES.find(([prefix]) => pathname.startsWith(prefix));
  return hit?.[1] ?? appName ?? 'TROIA';
}
import { SupportWidget } from './SupportWidget';
import { ThemeProvider } from '@/contexts/theme';
import { PrivacyProvider } from '@/contexts/PrivacyContext';
import { CompanyDrawerProvider } from '@/contexts/CompanyDrawerContext';
import { useSidebarPreferences } from '@/shared/hooks';
import { AppbarProvider, AppbarTitle } from '@/shared/components/layout/AppbarContext';
import { AppbarSearchButton, ModuleSearchOverlay, ModuleSearchProvider } from '@/shared/components/search/ModuleSearchContext';
import { CommandPalette } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useApp } from '@/contexts/AppContext';
import { api } from '@/lib/api';
import type { TenantThemeOverrides, AppResponse } from '@troia-v3/types';

export const MainLayout: React.FC = () => {
  const { isCollapsed, setIsCollapsed } = useSidebarPreferences();
  const { app } = useApp();
  const location = useLocation();
  // Drawer mobile: estado efêmero PRÓPRIO, desacoplado do rail do desktop
  // (senão o menu abre sozinho no load e o hambúrguer corromperia a pref de rail).
  const [mobileOpen, setMobileOpen] = useState(false);
  // Fecha o drawer mobile a cada navegação.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Fetch app data (includes baseThemeId + themeOverrides)
  const { data: appData } = useQuery<AppResponse | undefined>({
    queryKey: ['app-data'],
    queryFn: async () => {
      try {
        const response = await api.get('apps/me');
        return response.data.data as AppResponse;
      } catch {
        return undefined;
      }
    },
    staleTime: 1000 * 60 * 10,
    retry: false,
  });

  // Fetch company theme overrides
  const { data: companyOverrides } = useQuery<TenantThemeOverrides | undefined>({
    queryKey: ['tenant-theme'],
    queryFn: async () => {
      try {
        const response = await api.get('companies/theme');
        return response.data.data as TenantThemeOverrides;
      } catch {
        return undefined;
      }
    },
    staleTime: 1000 * 60 * 10,
    retry: false,
  });

  // isCollapsed = sidebar colapsada (minimizada)
  // isOpen = sidebar expandida (aberta)
  const sidebarOpen = !isCollapsed;

  const handleMenuClick = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleSidebarClose = () => {
    setIsCollapsed(true);
  };

  return (
    <ThemeProvider
      baseThemeId={appData?.baseThemeId}
      appOverrides={appData?.themeOverrides}
      companyOverrides={companyOverrides}
    >
      <PrivacyProvider>
       <AppbarProvider>
       <ModuleSearchProvider>
       {/* Estado da gaveta de Empresa: precisa envolver a busca ⌘K E a Sidebar
           (que renderiza a gaveta), pois as duas abrem o mesmo painel. */}
       <CompanyDrawerProvider>
        {/* Command Palette (Ctrl+K) */}
        <CommandPalette />

        {/* Busca do módulo (mobile) — barra no topo, sobrepõe a appbar (#007) */}
        <ModuleSearchOverlay />

        {/* Support Widget (injected dynamically if configured) */}
        <SupportWidget />

        <div className="h-screen flex overflow-hidden">
          {/* Sidebar (shell: absorve o antigo Header — F3) */}
          <Sidebar
            isOpen={sidebarOpen}
            onClose={handleSidebarClose}
            onToggle={handleMenuClick}
            mobileOpen={mobileOpen}
            onMobileClose={() => setMobileOpen(false)}
          />

          {/* Main content */}
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* `.m-appbar` do mockup (SÓ mobile): hambúrguer 38px + TÍTULO da rota
                18px bold + sino + busca (⌘K). O Header desktop foi removido (F3). */}
            <div
              className="min-[900px]:hidden flex items-center flex-shrink-0"
              style={{
                height: '54px',
                gap: '12px',
                padding: '0 14px',
                backgroundColor: 'var(--theme-header-background)',
                borderBottom: '1px solid var(--theme-header-border)',
              }}
            >
              <button
                onClick={() => setMobileOpen(true)}
                className="w-[38px] h-[38px] flex items-center justify-center rounded-[11px] flex-none"
                style={{ color: 'var(--theme-card-text)' }}
                aria-label="Abrir menu"
              >
                <Icon name="menu" className="h-[22px] w-[22px]" />
              </button>
              <AppbarTitle title={getRouteTitle(location.pathname, app?.name)} />
              <TeamChatButton size="md" />
              <NotificationsDropdown size="md" />
              <AppbarSearchButton />
            </div>

            {/* Aviso de canal desconectado — NO FLUXO, acima do conteúdo (R0.1) */}
            <DisconnectedChannelWidget />

            {/* Page content - SEMPRE full-width.
                Mobile: padding-bottom p/ o conteúdo não ficar sob a bottom-nav (R0.4). */}
            <main className="flex-1 overflow-auto pb-[72px] min-[900px]:pb-0">
              <RouteAccessGuard>
                <Outlet />
              </RouteAccessGuard>
            </main>
          </div>

          {/* Bottom-nav global do mobile (mockup: Atend | CRM | + | Inbox | Mais) — R0.4 */}
          <MobileNavBar />
        </div>
       </CompanyDrawerProvider>
       </ModuleSearchProvider>
       </AppbarProvider>
      </PrivacyProvider>
    </ThemeProvider>
  );
};